/**
 * Hand-rolled history routing — the design decision from intake-flow v1.1 §6, executed.
 *
 * WHY NOT A LIBRARY, restated where the code lives: the app has two routes and a bundle
 * ceiling with ~13 kB of headroom; `react-router` alone costs more than that. This file
 * is the whole router: parse, navigate, subscribe. If the app ever grows real
 * information architecture, replace it deliberately — do not grow this into a library.
 *
 * DEEP LINKS WORK because Cloudflare Pages serves the SPA shell for unmatched routes
 * (measured 26 Jul, re-verify after deploy per the standing note): a refresh on
 * /items/<id> loads the shell, and this parser takes it from there.
 */

export type Route =
  | { name: 'dashboard' }
  /**
   * `forCharacter`/`forProject` (0.1.395) put the Items tab into the same one-tap picking mode
   * `forOutfit`/`forBooking` already use — the character and project detail screens' "Pick an
   * existing item". `forProject` ALONE files the garment on the production with no character
   * named; the pair files it on the character (and `forProject` is what the back link needs to
   * rebuild the character route).
   */
  | {
      name: 'items';
      status?: string[] | undefined;
      forOutfit?: string | undefined;
      forBooking?: string | undefined;
      forCharacter?: string | undefined;
      forProject?: string | undefined;
      /**
       * PICK-A-GARMENT-FOR-A-BARCODE (0.1.515) — `?forBarcode=<code>`: the scan screen's "Link to
       * existing item" / "Link to another item". The list becomes a one-garment picker that links
       * the scanned code to the row you choose and returns to the scan. NOT a uuid — a barcode is
       * any short text a decoder hands back — so it is bounds-checked and URL-encoded instead.
       */
      forBarcode?: string | undefined;
    }
  /**
   * `from` is WHERE THE PERSON CAME FROM, carried in the URL so it survives a reload and
   * the browser's own back button. Without it every item detail returns to the item list,
   * which is wrong for the two screens that also list garments: an outfit and a box. Losing
   * your place in a 1,940-row list to check one piece is the whole complaint.
   */
  | {
      name: 'item';
      id: string;
      from?:
        | { kind: 'outfits' }
        | { kind: 'box'; id: string }
        | { kind: 'outfit'; id: string }
        // `booking` is the reservation an item was created for via "+ New item" (0.1.358). Like
        // `box`/`outfit` it exists ONLY so the item's back button returns to where you came from
        // — without it a new garment's back chip walked to the global Items list.
        | { kind: 'booking'; id: string }
        /**
         * The production or the person a garment was opened from (0.1.399). Both detail screens
         * list garments now, and without this the back chip walked to the global Items tab —
         * the same complaint that put `from` on this route in the first place, arriving again
         * from two new screens.
         */
        | { kind: 'project'; id: string }
        | { kind: 'character'; projectId: string; id: string }
    }
  /**
   * `outfitId` is the outfit an item is being CREATED FOR (0.1.171). A box is different in
   * kind — `boxId` becomes the item's `sourceOrderId` at birth, a fact about where the
   * garment came from — whereas an outfit is a named kit the new item joins after it exists.
   * They are two parameters rather than one because the API calls are two.
   */
  | {
      name: 'add';
      boxId?: string | undefined;
      outfitId?: string | undefined;
      /** The scan screen's "Link to new item" (0.1.515): the garment is born with this barcode. */
      barcode?: string | undefined;
    }
  /**
   * `floor` is the rack a placard's QR points at — `/boxes?floor=Rack%203` (053). It focuses
   * the board on one shelf, and it rides in the URL for the same reason the item list's status
   * does: a scanned link, a reload and a bookmark must all land on the same view. Absent on a
   * normal visit.
   */
  /** `forCharacter`/`forProject` (0.1.395) — the Boxes tab as a picker, the twin of the items
   *  and outfits modes above. Same rule: the pair files on the character, `forProject` alone
   *  files on the production. */
  | {
      name: 'boxes';
      floor?: string | undefined;
      forCharacter?: string | undefined;
      forProject?: string | undefined;
    }
  /**
   * The Rack location tab (054-follow). `/racks` is the overview (the room's racks and their
   * placards); `/racks?floor=Rack%203` is "everything on this rack" — the destination a
   * placard's QR now opens, spanning items, boxes and outfits (the boxes-only board is kept
   * separately, on its own screen).
   */
  | { name: 'racks'; floor?: string | undefined }
  | { name: 'retour' }
  | { name: 'boxAdd' }
  | {
      name: 'box';
      id: string;
      /** Where the parcel was opened from (0.1.399) — a production or one of its people. Without
       *  it the back chip walks to the Boxes board, losing the place you were working in. */
      from?: { kind: 'project'; id: string } | { kind: 'character'; projectId: string; id: string };
    }
  /**
   * `from` is the CHARACTER an outfit was opened from (0.1.390) — the same idea `item.from` has
   * carried since the redesign, for the same reason: without it the back chip walks to the global
   * Outfits list, and a person filling a character's six slots loses their place on every peek.
   * Carried in the URL so it survives a reload and the browser's own back button.
   */
  | {
      name: 'outfit';
      id: string;
      from?:
        | { kind: 'character'; projectId: string; id: string }
        /** A look opened from the production's own Outfits subsection (0.1.399). */
        | { kind: 'project'; id: string }
        /** A look opened from a reservation's Outfits section (0.1.523; maintainer, 1 Oct 2026: "be
         *  able to click on an outfit row in a reservation and it should take me to that outfit,
         *  with a back button back to the reservation"). */
        | { kind: 'booking'; id: string };
    }
  /** The Projects tab (063) and its two detail levels. A character carries its `projectId` in
   *  the path so its back chip and its re-read (`listCharacters(projectId)`) survive a reload. */
  | { name: 'projects' }
  | { name: 'project'; id: string }
  | { name: 'character'; projectId: string; id: string }
  | { name: 'settings' }
  | { name: 'users' }
  /**
   * PICK-A-RESERVATION-FOR-A-PRODUCTION (0.1.523) — `?forProject=<uuid>` (and `&forCharacter=<uuid>`):
   * the project/character detail's "Pick an existing reservation". Tapping a reservation files it
   * there and walks back — the Outfits tab's picker shape.
   */
  | { name: 'reservations'; forProject?: string | undefined; forCharacter?: string | undefined }
  | { name: 'checkouts' }
  /**
   * ⚠️ `from` EXISTS FOR THE BACK BUTTON AND NOTHING ELSE — 0.1.179, reported from use.
   *
   * `/bookings/:id` serves both tabs. Opening a row from Check-outs landed on the detail
   * with a "← Reservations" button, which walked the person to the OTHER tab — so getting
   * back to the list they came from took two presses and knowing which tab to pick.
   *
   * Carried in the URL (`?from=checkouts`) rather than in memory, deliberately: a reload, a
   * shared link and the browser's own back button all have to agree with the button on the
   * screen, and state held in a component survives none of those.
   */
  | {
      name: 'booking';
      id: string;
      from?: 'checkouts';
      /** Opened from a project's or a character's Reservations section (0.1.523) — the back chip
       *  returns there. Its own field so `from: 'checkouts'` and every URL built from it stay as they were. */
      origin?: { kind: 'project'; id: string } | { kind: 'character'; projectId: string; id: string } | undefined;
    }
  /**
   * `/bookings/:id/checkin` — the per-garment check-in screen (frame 4d). Its own route,
   * not a mode of the booking detail, because it is a focused act with its own header
   * ("← Check-outs") and floating "Check in N": a reload or a shared link must land back
   * on the check-in, and the two "Check in…" doors on the Check-outs board point here.
   */
  | { name: 'booking-checkin'; id: string }
  | { name: 'maintenance' }
  /**
   * `linked` (0.1.515): the picker's way back after "Link to existing item" — the scan screen
   * shows that garment as its result, headed "Linked", camera one tap away (maintainer ruling,
   * 27 Sep 2026: back to Scan, for the rack-by-rack round). In the URL so a reload keeps it.
   */
  | { name: 'scan'; linked?: { code: string; itemId: string } | undefined }
  /**
   * PICK-AN-OUTFIT-FOR-A-CHARACTER (0.1.382) — `?forCharacter=<uuid>&forProject=<uuid>&slot=<n>`
   * puts the whole Outfits tab into a one-tap picking mode: an empty outfit-slot on a character
   * sends you here, tapping a card FILES that look into the slot, and you land back on the
   * character. The same shape `items?forOutfit=` already uses, for the same reason — the picker is
   * the real list, not a cut-down copy of it that drifts.
   *
   * `forProject` rides along because the character route needs it to navigate back; `slot` is the
   * number the empty slot stood for, so the filing lands on the row that was tapped.
   */
  | {
      name: 'outfits';
      forCharacter?: string | undefined;
      forProject?: string | undefined;
      slot?: number | undefined;
      /** PICK-A-LOOK-FOR-A-RESERVATION (0.1.523) — `?forBooking=<uuid>`: the reservation's "Add
       *  existing outfit" tile. Tapping a look adds it to that reservation and walks back. */
      forBooking?: string | undefined;
    }
  | { name: 'import' }
  /**
   * ⚠️ THE ONE PUBLIC ROUTE IN THE APP (0.1.209) — `/join?token=…`, where somebody with no
   * account and no seat accepts an invitation. Every other member of this union assumes an
   * atelier user; this one assumes a stranger, which is why `app.tsx` renders it BEFORE the
   * auth gate rather than inside it.
   *
   * ⚠️ THE TOKEN IS CARRIED, NOT VALIDATED, AND `null` IS A REAL VALUE. A mail client that
   * wrapped the URL, or somebody typing `/join` by hand, must reach the screen and be told the
   * link is incomplete — falling through to the dashboard would sign-in-wall a person who was
   * invited, with no explanation. Only the server can say whether a token is good.
   */
  | { name: 'join'; token: string | null }
  /**
   * ⚠️ `clientView` STOOD HERE — `/v/<id>`, the one PUBLIC route, rendered outside the
   * signed-in shell because everything else in this union assumes an atelier user and that
   * one assumed nobody. Withdrawn 6 Aug 2026 with the client view itself (migration 038).
   *
   * A `/v/...` path now falls through to the dashboard fallback like any other unknown
   * path, which is safe only because there is no longer an unauthenticated screen for it to
   * miss: an old client link lands a SIGNED-OUT visitor on the sign-in screen, and a client
   * has no account, so it ends there. That is the intended dead end.
   */;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A scanned code in a URL (0.1.515): any non-blank text up to the server's 200-character bound. */
function barcodeParam(value: string | null): string | undefined {
  return value !== null && value.trim() !== '' && value.length <= 200 ? value : undefined;
}

/** Unknown paths render the DASHBOARD rather than a dead end — since 0.1.39 it is
 * the landing screen (maintainer ruling, 28 Jul: "Dashboard as the landing screen"),
 * and a typo'd URL should land somewhere useful, not on a 404 we'd have to build.
 * Pure, so the mapping is testable without a DOM. Accepts `pathname` or
 * `pathname?search` — the only query parameter is the item list's status filter,
 * which the dashboard's cards use to deep-link pre-filtered. */
export function parseRoute(url: string): Route {
  const [pathOnly = '', search = ''] = url.split('?');
  const parts = pathOnly.split('/').filter((p) => p !== '');
  if (parts.length === 1 && parts[0] === 'items') {
    const q = new URLSearchParams(search);
    // PICK-FOR-A-BARCODE (0.1.515) — first, like every picker: it is a screen mode, not a filter.
    const forBarcode = barcodeParam(q.get('forBarcode'));
    if (forBarcode !== undefined) return { name: 'items', forBarcode };
    // PICK-FOR-A-LOOK (frame 5c "Add an existing item"): `?forOutfit=<uuid>` puts the whole
    // Items screen in a mode that files what you tick into that outfit. Checked first because it
    // is a distinct screen mode, not just a pre-applied filter.
    const forOutfit = q.get('forOutfit');
    if (forOutfit !== null && UUID.test(forOutfit)) return { name: 'items', forOutfit };
    // PICK-FOR-A-RESERVATION (frame "Picking garments for this reservation"): the booking twin of
    // forOutfit — `?forBooking=<uuid>` files what you tick into that reservation via addBookingItems.
    const forBooking = q.get('forBooking');
    if (forBooking !== null && UUID.test(forBooking)) return { name: 'items', forBooking };
    // PICK-FOR-A-CHARACTER / PICK-FOR-A-PRODUCTION (0.1.395) — the same mode again, from the
    // character and project detail screens. The pair is checked before the project-only form, so
    // `?forCharacter=&forProject=` is never read as the weaker of the two.
    const forCharacter = q.get('forCharacter');
    const forProject = q.get('forProject');
    if (forCharacter !== null && UUID.test(forCharacter) && forProject !== null && UUID.test(forProject)) {
      return { name: 'items', forCharacter, forProject };
    }
    if (forProject !== null && UUID.test(forProject)) return { name: 'items', forProject };
    // Status is a SET now (frame 3c multi-select): comma-separated in the URL, so a dashboard
    // deep-link (`?status=flagged`) and a multi-pick filter (`?status=flagged,available`) both
    // round-trip. Empty segments are dropped so a stray comma cannot become a "" status.
    const raw = q.get('status');
    const status = raw === null ? [] : raw.split(',').filter((s) => s !== '');
    return status.length === 0 ? { name: 'items' } : { name: 'items', status };
  }
  if (parts.length === 2 && parts[0] === 'items' && UUID.test(parts[1] ?? '')) {
    // `?box=` reuses the parameter `/add` already uses for the same idea, rather than
    // inventing a second spelling of "which box are we in".
    const q = new URLSearchParams(search);
    const box = q.get('box');
    const outfit = q.get('outfit');
    const booking = q.get('booking');
    if (box !== null && UUID.test(box)) return { name: 'item', id: parts[1]!, from: { kind: 'box', id: box } };
    // `?booking=<id>` — created from a reservation's "+ New item"; back returns to that reservation.
    if (booking !== null && UUID.test(booking)) return { name: 'item', id: parts[1]!, from: { kind: 'booking', id: booking } };
    // `?outfit=<id>` — opened from a SPECIFIC look's detail; back returns to that look, not the
    // outfits list. (`?from=outfits` is the list itself — back to the list.)
    if (outfit !== null && UUID.test(outfit)) return { name: 'item', id: parts[1]!, from: { kind: 'outfit', id: outfit } };
    // The two structure origins (0.1.399). The character form needs its project too, because the
    // character route is nested under one — the pair is checked first for that reason.
    const fromCharacter = q.get('fromCharacter');
    const fromProject = q.get('fromProject');
    if (fromCharacter !== null && UUID.test(fromCharacter) && fromProject !== null && UUID.test(fromProject)) {
      return { name: 'item', id: parts[1]!, from: { kind: 'character', projectId: fromProject, id: fromCharacter } };
    }
    if (fromProject !== null && UUID.test(fromProject)) {
      return { name: 'item', id: parts[1]!, from: { kind: 'project', id: fromProject } };
    }
    if (q.get('from') === 'outfits') return { name: 'item', id: parts[1]!, from: { kind: 'outfits' } };
    return { name: 'item', id: parts[1]! };
  }
  if (parts.length === 1 && parts[0] === 'add') {
    const q = new URLSearchParams(search);
    const boxId = q.get('box');
    const outfitId = q.get('outfit');
    if (boxId !== null && UUID.test(boxId)) return { name: 'add', boxId };
    if (outfitId !== null && UUID.test(outfitId)) return { name: 'add', outfitId };
    const barcode = barcodeParam(q.get('barcode'));
    if (barcode !== undefined) return { name: 'add', barcode };
    return { name: 'add' };
  }
  if (parts.length === 1 && parts[0] === 'boxes') {
    const q = new URLSearchParams(search);
    // PICK-A-BOX (0.1.395) — checked before `floor`, because a picking board is a screen MODE and
    // a focus is a filter within the ordinary board. Same two forms as the items picker.
    const forCharacter = q.get('forCharacter');
    const forProject = q.get('forProject');
    if (forCharacter !== null && UUID.test(forCharacter) && forProject !== null && UUID.test(forProject)) {
      return { name: 'boxes', forCharacter, forProject };
    }
    if (forProject !== null && UUID.test(forProject)) return { name: 'boxes', forProject };
    // A blank `?floor=` is the same as no focus — the placard's link did not survive the
    // trip, and the honest answer is the whole board, not a focus on nothing.
    const raw = q.get('floor');
    const floor = raw !== null && raw.trim() !== '' ? raw : undefined;
    return floor === undefined ? { name: 'boxes' } : { name: 'boxes', floor };
  }
  if (parts.length === 1 && parts[0] === 'racks') {
    const raw = new URLSearchParams(search).get('floor');
    const floor = raw !== null && raw.trim() !== '' ? raw : undefined;
    return floor === undefined ? { name: 'racks' } : { name: 'racks', floor };
  }
  if (parts.length === 1 && parts[0] === 'retour') return { name: 'retour' };
  if (parts.length === 2 && parts[0] === 'boxes' && parts[1] === 'new') return { name: 'boxAdd' };
  if (parts.length === 2 && parts[0] === 'boxes' && UUID.test(parts[1] ?? '')) {
    const q = new URLSearchParams(search);
    const fromCharacter = q.get('fromCharacter');
    const fromProject = q.get('fromProject');
    if (fromCharacter !== null && UUID.test(fromCharacter) && fromProject !== null && UUID.test(fromProject)) {
      return { name: 'box', id: parts[1]!, from: { kind: 'character', projectId: fromProject, id: fromCharacter } };
    }
    if (fromProject !== null && UUID.test(fromProject)) {
      return { name: 'box', id: parts[1]!, from: { kind: 'project', id: fromProject } };
    }
    return { name: 'box', id: parts[1]! };
  }
  if (parts.length === 2 && parts[0] === 'outfits' && UUID.test(parts[1] ?? '')) {
    const q = new URLSearchParams(search);
    const fromCharacter = q.get('fromCharacter');
    const fromProject = q.get('fromProject');
    if (fromCharacter !== null && UUID.test(fromCharacter) && fromProject !== null && UUID.test(fromProject)) {
      return { name: 'outfit', id: parts[1]!, from: { kind: 'character', projectId: fromProject, id: fromCharacter } };
    }
    if (fromProject !== null && UUID.test(fromProject)) {
      return { name: 'outfit', id: parts[1]!, from: { kind: 'project', id: fromProject } };
    }
    const fromBooking = q.get('fromBooking');
    if (fromBooking !== null && UUID.test(fromBooking)) {
      return { name: 'outfit', id: parts[1]!, from: { kind: 'booking', id: fromBooking } };
    }
    return { name: 'outfit', id: parts[1]! };
  }
  // Projects (063): the tab, a project detail, and a character detail nested under its project.
  if (parts.length === 1 && parts[0] === 'projects') return { name: 'projects' };
  if (parts.length === 2 && parts[0] === 'projects' && UUID.test(parts[1] ?? '')) {
    return { name: 'project', id: parts[1]! };
  }
  if (
    parts.length === 4 &&
    parts[0] === 'projects' &&
    UUID.test(parts[1] ?? '') &&
    parts[2] === 'characters' &&
    UUID.test(parts[3] ?? '')
  ) {
    return { name: 'character', projectId: parts[1]!, id: parts[3]! };
  }
  // '/fields' is kept as an alias: it was the URL all through 0.1.18 and a silent
  // fallback to the item list is how a bookmark dies quietly.
  /**
   * `/structure` and `/fields` were the catalogue-structure admin screen, removed in
   * 0.1.86 once the same editing lived on the field rows and inside the category and
   * location pickers. They resolve to the item list rather than 404: both were bookmarked
   * and linked from older notes, and a dead URL is a worse answer than the list.
   */
  if (parts.length === 1 && (parts[0] === 'structure' || parts[0] === 'fields')) return { name: 'items' };
  if (parts.length === 1 && parts[0] === 'settings') return { name: 'settings' };
  if (parts.length === 1 && parts[0] === 'users') return { name: 'users' };
  if (parts.length === 3 && parts[0] === 'bookings' && UUID.test(parts[1] ?? '') && parts[2] === 'checkin') {
    return { name: 'booking-checkin', id: parts[1]! };
  }
  if (parts.length === 2 && parts[0] === 'bookings' && UUID.test(parts[1] ?? '')) {
    // Anything other than `checkouts` is ignored rather than carried: an unknown value would
    // otherwise reach `routePath` and be written back into the URL unchallenged.
    const q = new URLSearchParams(search);
    const from = q.get('from');
    if (from === 'checkouts') return { name: 'booking', id: parts[1]!, from: 'checkouts' };
    const fromCharacter = q.get('fromCharacter');
    const fromProject = q.get('fromProject');
    if (fromCharacter !== null && UUID.test(fromCharacter) && fromProject !== null && UUID.test(fromProject)) {
      return { name: 'booking', id: parts[1]!, origin: { kind: 'character', projectId: fromProject, id: fromCharacter } };
    }
    if (fromProject !== null && UUID.test(fromProject)) {
      return { name: 'booking', id: parts[1]!, origin: { kind: 'project', id: fromProject } };
    }
    return { name: 'booking', id: parts[1]! };
  }
  if (parts.length === 1 && parts[0] === 'reservations') {
    const q = new URLSearchParams(search);
    const forProject = q.get('forProject');
    const forCharacter = q.get('forCharacter');
    if (forProject !== null && UUID.test(forProject)) {
      return forCharacter !== null && UUID.test(forCharacter)
        ? { name: 'reservations', forProject, forCharacter }
        : { name: 'reservations', forProject };
    }
    return { name: 'reservations' };
  }
  if (parts.length === 1 && parts[0] === 'checkouts') return { name: 'checkouts' };
  // `/bookings` split into two tabs in 0.1.158 and is KEPT as an alias rather than
  // dropped: it is the path in every previously-sent client link, in the PWA's saved
  // shortcuts and in a year of the maintainer's muscle memory. Unknown paths fall through
  // to the dashboard, so removing it would have turned an old link into a silent wrong
  // screen — which is the shape this project calls a failure with no signal.
  if (parts.length === 1 && parts[0] === 'bookings') return { name: 'reservations' };
  if (parts.length === 1 && parts[0] === 'maintenance') return { name: 'maintenance' };
  if (parts.length === 1 && parts[0] === 'scan') {
    const q = new URLSearchParams(search);
    const code = barcodeParam(q.get('linked'));
    const itemId = q.get('item');
    if (code !== undefined && itemId !== null && UUID.test(itemId)) return { name: 'scan', linked: { code, itemId } };
    return { name: 'scan' };
  }
  if (parts.length === 1 && parts[0] === 'outfits') {
    const q = new URLSearchParams(search);
    const forCharacter = q.get('forCharacter');
    const forProject = q.get('forProject');
    const slotRaw = q.get('slot');
    if (forCharacter !== null && UUID.test(forCharacter) && forProject !== null && UUID.test(forProject)) {
      const slot = slotRaw !== null && /^\d{1,3}$/.test(slotRaw) ? Number(slotRaw) : undefined;
      return { name: 'outfits', forCharacter, forProject, slot };
    }
    // PICK-A-LOOK-FOR-A-PRODUCTION (0.1.395) — the project detail's own "Pick an existing
    // outfit". No character and therefore no slot: the number belongs to a character's run of
    // looks, and there is no character here to number against.
    if (forProject !== null && UUID.test(forProject)) return { name: 'outfits', forProject };
    const forBooking = q.get('forBooking');
    if (forBooking !== null && UUID.test(forBooking)) return { name: 'outfits', forBooking };
    return { name: 'outfits' };
  }
  if (parts.length === 1 && parts[0] === 'import') return { name: 'import' };
  if (parts.length === 1 && parts[0] === 'join') {
    // An empty `?token=` is the same thing as no token at all — both mean the link did not
    // survive the trip, and both get the "incomplete link" screen rather than a redeem attempt.
    const token = new URLSearchParams(search).get('token');
    return { name: 'join', token: token !== null && token !== '' ? token : null };
  }
  return { name: 'dashboard' };
}

export function routePath(route: Route): string {
  if (route.name === 'outfits' && route.forCharacter !== undefined && route.forProject !== undefined) {
    const slot = route.slot === undefined ? '' : `&slot=${String(route.slot)}`;
    return `/outfits?forCharacter=${route.forCharacter}&forProject=${route.forProject}${slot}`;
  }
  if (route.name === 'outfits' && route.forProject !== undefined) {
    return `/outfits?forProject=${route.forProject}`;
  }
  if (route.name === 'outfits' && route.forBooking !== undefined) {
    return `/outfits?forBooking=${route.forBooking}`;
  }
  if (route.name === 'items') {
    if (route.forBarcode !== undefined) return `/items?forBarcode=${encodeURIComponent(route.forBarcode)}`;
    if (route.forOutfit !== undefined) return `/items?forOutfit=${route.forOutfit}`;
    if (route.forBooking !== undefined) return `/items?forBooking=${route.forBooking}`;
    if (route.forCharacter !== undefined && route.forProject !== undefined) {
      return `/items?forCharacter=${route.forCharacter}&forProject=${route.forProject}`;
    }
    if (route.forProject !== undefined) return `/items?forProject=${route.forProject}`;
    return route.status === undefined || route.status.length === 0
      ? '/items'
      : `/items?status=${encodeURIComponent(route.status.join(','))}`;
  }
  if (route.name === 'item') {
    if (route.from?.kind === 'character') {
      return `/items/${route.id}?fromCharacter=${route.from.id}&fromProject=${route.from.projectId}`;
    }
    if (route.from?.kind === 'project') return `/items/${route.id}?fromProject=${route.from.id}`;
    if (route.from?.kind === 'box') return `/items/${route.id}?box=${route.from.id}`;
    if (route.from?.kind === 'outfit') return `/items/${route.id}?outfit=${route.from.id}`;
    if (route.from?.kind === 'booking') return `/items/${route.id}?booking=${route.from.id}`;
    if (route.from?.kind === 'outfits') return `/items/${route.id}?from=outfits`;
    return `/items/${route.id}`;
  }
  if (route.name === 'add') {
    if (route.boxId !== undefined) return `/add?box=${route.boxId}`;
    if (route.outfitId !== undefined) return `/add?outfit=${route.outfitId}`;
    if (route.barcode !== undefined) return `/add?barcode=${encodeURIComponent(route.barcode)}`;
    return '/add';
  }
  if (route.name === 'boxes') {
    if (route.forCharacter !== undefined && route.forProject !== undefined) {
      return `/boxes?forCharacter=${route.forCharacter}&forProject=${route.forProject}`;
    }
    if (route.forProject !== undefined) return `/boxes?forProject=${route.forProject}`;
    return route.floor === undefined ? '/boxes' : `/boxes?floor=${encodeURIComponent(route.floor)}`;
  }
  if (route.name === 'racks') {
    return route.floor === undefined ? '/racks' : `/racks?floor=${encodeURIComponent(route.floor)}`;
  }
  if (route.name === 'retour') return '/retour';
  if (route.name === 'boxAdd') return '/boxes/new';
  if (route.name === 'box') {
    if (route.from?.kind === 'character') {
      return `/boxes/${route.id}?fromCharacter=${route.from.id}&fromProject=${route.from.projectId}`;
    }
    if (route.from?.kind === 'project') return `/boxes/${route.id}?fromProject=${route.from.id}`;
    return `/boxes/${route.id}`;
  }
  if (route.name === 'outfit') {
    return route.from === undefined
      ? `/outfits/${route.id}`
      : route.from.kind === 'project'
        ? `/outfits/${route.id}?fromProject=${route.from.id}`
        : route.from.kind === 'booking'
          ? `/outfits/${route.id}?fromBooking=${route.from.id}`
          : `/outfits/${route.id}?fromCharacter=${route.from.id}&fromProject=${route.from.projectId}`;
  }
  if (route.name === 'projects') return '/projects';
  if (route.name === 'project') return `/projects/${route.id}`;
  if (route.name === 'character') return `/projects/${route.projectId}/characters/${route.id}`;
  if (route.name === 'settings') return '/settings';
  if (route.name === 'users') return '/users';
  if (route.name === 'reservations') {
    if (route.forProject === undefined) return '/reservations';
    return route.forCharacter === undefined
      ? `/reservations?forProject=${route.forProject}`
      : `/reservations?forProject=${route.forProject}&forCharacter=${route.forCharacter}`;
  }
  if (route.name === 'checkouts') return '/checkouts';
  if (route.name === 'booking') {
    if (route.from === 'checkouts') return `/bookings/${route.id}?from=checkouts`;
    if (route.origin?.kind === 'character') {
      return `/bookings/${route.id}?fromCharacter=${route.origin.id}&fromProject=${route.origin.projectId}`;
    }
    if (route.origin?.kind === 'project') return `/bookings/${route.id}?fromProject=${route.origin.id}`;
    return `/bookings/${route.id}`;
  }
  if (route.name === 'booking-checkin') return `/bookings/${route.id}/checkin`;
  if (route.name === 'maintenance') return '/maintenance';
  if (route.name === 'scan') {
    return route.linked === undefined
      ? '/scan'
      : `/scan?linked=${encodeURIComponent(route.linked.code)}&item=${route.linked.itemId}`;
  }
  if (route.name === 'outfits') return '/outfits';
  if (route.name === 'import') return '/import';
  if (route.name === 'join') {
    // Round-trips: a token parsed out of a URL must produce the same URL, or `navigate` would
    // silently drop the credential the screen is about to redeem.
    return route.token === null ? '/join' : `/join?token=${encodeURIComponent(route.token)}`;
  }
  return '/';
}

type Listener = (route: Route) => void;
const listeners = new Set<Listener>();

function current(): Route {
  return parseRoute(window.location.pathname + window.location.search);
}

function notify(): void {
  const route = current();
  for (const listener of listeners) listener(route);
}

export function navigate(route: Route, opts: { replace?: boolean } = {}): void {
  const path = routePath(route);
  /**
   * ⚠️ COMPARE THE WHOLE URL, `pathname + search` — NOT `pathname` alone (0.1.403).
   *
   * `current()` two functions up parses `pathname + window.location.search`, so the QUERY STRING
   * IS PART OF THE ROUTE: `/racks?floor=Rack 3` is a different screen from `/racks`, and so are
   * `/boxes?floor=…`, `/items?status=…`, `/items?forProject=…` and `/join?token=…`. Comparing
   * only the path made every query-only move a silent no-op — no history entry, the old search
   * left standing, and `notify()` re-deriving the SAME route from the unchanged URL.
   *
   * What that cost in the field: the rack detail's "← Rack location" back button did nothing at
   * all (maintainer, 27 Aug). It was not a dead link and not a styling problem — the anchor was
   * hit, `preventDefault` ran, `navigate({name:'racks'})` was called, and this comparison threw
   * the move away because `/racks` === `/racks`.
   *
   * The rule the two functions must share: whatever `current()` READS from the URL, this must
   * WRITE to it. Adding a route field to the query string without touching this line is the way
   * back into the bug.
   */
  if (window.location.pathname + window.location.search !== path) {
    // replace: for a route whose visit HAS a side effect (/boxes/new creates the box,
    // 0.1.267) — Back must return to where the person was, never re-fire the visit.
    const here = historyIndex() ?? 0;
    const idx = opts.replace === true ? here : here + 1;
    if (opts.replace === true) window.history.replaceState({ mk: idx }, '', path);
    else window.history.pushState({ mk: idx }, '', path);
    rememberPath(idx, path, opts.replace !== true);
  }
  notify();
}

/**
 * ⚠️ WHERE "BACK" GOES: THE SCREEN YOU CAME FROM (0.1.524; maintainer, 2 Oct 2026: *"the back
 * button goes back to the screen I just came from … when I'm in a reservation screen and I click on
 * an item … the back button then takes me to the item list instead of back to my reservation"*).
 *
 * Until now every on-screen back chip navigated FORWARD to a fixed parent picked from the route's
 * closed `from`/`origin` set. Any path nobody had enumerated — reservation → item, scan → item,
 * dashboard → item, a conflict → reservation — and every chain of two hops walked somewhere the
 * person had never been. The browser's own Back was always right, because it pops real history.
 * So the chips now do the same: `goBack` pops history when there IS an in-app screen behind this
 * one, and only a fresh visit (a link, a scan, a new tab) falls back to the route's parent.
 *
 * HOW "IS THERE A SCREEN BEHIND" IS ANSWERED. History hides its entries, so every entry this
 * router writes carries its depth in `history.state.mk`, and the path at each depth is mirrored in
 * sessionStorage (so the chip's LABEL can name the screen behind, and survives a reload — history
 * state does too). A state without `mk` — a fresh tab, or `auth.ts` scrubbing the sign-in URL with
 * `replaceState(null)` — reads as depth 0: nothing behind, use the fallback. The overlay sentinel
 * (`use-history-dismiss.ts`) copies `mk`, so a sheet being open does not change the answer.
 */
const NAV_KEY = 'mononoke.nav.v1';
/** Depths kept behind the current one — a long session must not grow storage without bound. */
const NAV_KEEP = 100;

export function historyIndex(): number | null {
  const state = window.history.state as { mk?: unknown } | null;
  return typeof state?.mk === 'number' && Number.isInteger(state.mk) && state.mk >= 0 ? state.mk : null;
}

function readPaths(): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(NAV_KEY) ?? '{}');
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) if (typeof v === 'string') out[k] = v;
    return out;
  } catch {
    return {};
  }
}

function writePaths(paths: Record<string, string>): void {
  try {
    window.sessionStorage.setItem(NAV_KEY, JSON.stringify(paths));
  } catch {
    // Private mode / blocked storage: the chips fall back to each route's parent. Never fatal.
  }
}

/** Record `path` at depth `idx`. A push also forgets every deeper entry — they are the old
 *  forward history, which the browser has just discarded too. */
function rememberPath(idx: number, path: string, dropForward: boolean): void {
  const paths = readPaths();
  const next: Record<string, string> = {};
  for (const [k, v] of Object.entries(paths)) {
    const n = Number(k);
    if (n < idx - NAV_KEEP) continue;
    if (dropForward && n > idx) continue;
    next[k] = v;
  }
  next[String(idx)] = path;
  writePaths(next);
}

/** Stamp the entry the app was opened on, so the first in-app push has a depth to count from.
 *  An entry that already carries one (a reload) is left alone. */
export function stampHistoryEntry(): void {
  const path = window.location.pathname + window.location.search;
  const idx = historyIndex();
  if (idx === null) {
    const state = (window.history.state as Record<string, unknown> | null) ?? {};
    window.history.replaceState({ ...state, mk: 0 }, '', path);
    writePaths({ '0': path });
  } else if (readPaths()[String(idx)] === undefined) {
    rememberPath(idx, path, false);
  }
}

/** The in-app screen behind this one, or null on a fresh visit (nothing behind, or unreadable). */
export function previousRoute(): Route | null {
  const idx = historyIndex();
  if (idx === null || idx < 1) return null;
  const path = readPaths()[String(idx - 1)];
  return path === undefined ? null : parseRoute(path);
}

/** The back chip: return to the previous screen; on a fresh visit, go to `fallback`. */
export function goBack(fallback: Route): void {
  // ⚠️ A SELECTION'S SENTINEL MAY BE ON TOP (0.1.527, `SelectionBackGuard`): a back chip pressed
  // with rows ticked must still go back, so it steps over the sentinel (-2) — the coordinator sees
  // that one popstate, clears the selection and drops its listener; the router sees the real route.
  const overlay = (window.history.state as { __overlay?: unknown } | null)?.__overlay === true;
  if (previousRoute() !== null) {
    if (overlay) window.history.go(-2);
    else window.history.back();
  }
  else navigate(fallback);
}

/**
 * Leave a screen whose errand is FINISHED or whose subject is GONE — a delete, a picker's confirm.
 * Same destination as `goBack`, but the fresh-visit fallback REPLACES this entry: Back must never
 * land on a deleted garment or re-open a picker whose pick is done.
 */
export function leave(fallback: Route): void {
  const go = (): void => {
    if (previousRoute() !== null) window.history.back();
    else navigate(fallback, { replace: true });
  };
  /**
   * ⚠️ AN OPEN SHEET'S SENTINEL IS STILL ON TOP when most of these run: "Delete…" lives in the
   * Actions sheet, and the sheet closes in the SAME handler (`setActsOpen(false)`), consuming its
   * sentinel with its own `history.back()` a render later. A second `back()` from here in that
   * window would race it — two queued traversals, and whether the browser runs both decides
   * whether the person lands where they came from or on the thing they just deleted. So wait for
   * the sentinel's pop, then leave from the real entry. If no pop comes (nothing was closing),
   * go to the fallback rather than guess at the history.
   */
  const state = window.history.state as { __overlay?: unknown } | null;
  if (state?.__overlay !== true) {
    go();
    return;
  }
  let done = false;
  const onPop = (): void => {
    if (done) return;
    done = true;
    window.removeEventListener('popstate', onPop);
    window.clearTimeout(timer);
    // The overlay coordinator handles this same popstate; leave on the next tick, from the page.
    window.setTimeout(go, 0);
  };
  const timer = window.setTimeout(() => {
    if (done) return;
    done = true;
    window.removeEventListener('popstate', onPop);
    navigate(fallback, { replace: true });
  }, 1000);
  window.addEventListener('popstate', onPop);
}

/** Subscribe to route changes; returns the unsubscribe. Fires immediately with the
 * current route so callers do not need a separate initial read. */
export function subscribeRoute(listener: Listener): () => void {
  stampHistoryEntry();
  listeners.add(listener);
  listener(current());
  const onPop = (): void => {
    notify();
  };
  window.addEventListener('popstate', onPop);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', onPop);
  };
}
